import {
  Spin,
  List,
  Card,
  Row,
  Button,
  Modal,
  Space,
  message,
  Image,
  Switch,
  Checkbox,
  Badge,
  Select,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Locale } from "../../utils";
import {
  handleMutationError,
  showNotificationWithIcon,
} from "../../utils/helpers/helpers";
import { getList, remove, update } from "../../utils/http/dataProvider";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { BreadCrumb } from "../../components/main/LayoutComponents";

import "./VideoList.scss";
import VideoListHeader from "./VideoListHeader";

const i18n = Locale.getInstance();
const { Meta } = Card;
const { Option } = Select;

const formatDuration = (seconds) => {
  if (!seconds) return "00:00";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

const VideoList = () => {
  const user = useSelector((state) => getUserInfo(state));
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState();
  const [status, setStatus] = useState(undefined);
  const [categoryId, setCategoryId] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchCategoryModalVisible, setBatchCategoryModalVisible] =
    useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(undefined);
  const history = useHistory();
  const [perPage, setPerPage] = useState(12);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [keyword, status, categoryId]);

  const filter = {};
  if (keyword) filter.keyword = keyword;
  if (status !== undefined) filter.status = status;
  // Do not send video_category_id when "all": 0 is often treated as "uncategorized only".
  if (categoryId > 0) {
    filter.video_category_id = categoryId;
  }
  // Flat param for backends that read $request->video_category_id (not filter[]).
  const listQuery =
    categoryId > 0 ? { video_category_id: categoryId } : {};

  const { data, error, isLoading } = useQuery(
    ["videos", user.token, page, perPage, filter, listQuery],
    () =>
      getList("video", {
        pagination: {
          perPage,
          page,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        filter,
        query: listQuery,
        token: user.token,
      })
  );

  if (error) {
    showNotificationWithIcon(error.data?.message || error.message);
  }

  const { data: categoriesData } = useQuery(
    ["video-categories", user.token],
    () =>
      getList("video-category", {
        pagination: {
          perPage: 1000,
          page: 1,
        },
        sort: {
          field: "id",
          order: "desc",
        },
        token: user.token,
      })
  );

  const categories = categoriesData?.data ?? [];

  const deleteMutation = useMutation(remove, {
    onSuccess: () => {
      queryClient.invalidateQueries("videos");
      setLoading(false);
      showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
    },
    onError: (error) => {
      setLoading(false);
      handleMutationError(error);
    },
  });

  const batchUpdateMutation = useMutation(
    async ({ ids, data: updateData }) => {
      const promises = ids.map((id) =>
        update({
          resource: "video",
          params: {
            id,
            data: updateData,
            token: user.token,
          },
        })
      );
      return Promise.all(promises);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("videos");
        setSelectedRowKeys([]);
        setLoading(false);
        setBatchCategoryModalVisible(false);
        setSelectedCategoryId(undefined);
        showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const handleBatchSetCategory = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("video.batchSelectWarning"));
      return;
    }
    setBatchCategoryModalVisible(true);
  };

  const handleBatchCategoryConfirm = () => {
    if (!selectedCategoryId) {
      message.warning(i18n.t("course.selectCategoryWarning"));
      return;
    }
    setLoading(true);
    batchUpdateMutation.mutate({
      ids: selectedRowKeys,
      data: { video_category_id: selectedCategoryId },
    });
  };

  const batchDeleteMutation = useMutation(
    async (ids) => {
      const promises = ids.map((id) =>
        remove({
          resource: "video",
          params: {
            id,
            token: user.token,
          },
        })
      );
      return Promise.all(promises);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("videos");
        setSelectedRowKeys([]);
        setLoading(false);
        showNotificationWithIcon(i18n.t("media.saveFinished"), "success");
      },
      onError: (error) => {
        setLoading(false);
        handleMutationError(error);
      },
    }
  );

  const handleBatchOnShelf = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("video.batchSelectWarning"));
      return;
    }
    setLoading(true);
    batchUpdateMutation.mutate({
      ids: selectedRowKeys,
      data: { status: 1 },
    });
  };

  const handleBatchOffShelf = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("video.batchSelectWarning"));
      return;
    }
    setLoading(true);
    batchUpdateMutation.mutate({
      ids: selectedRowKeys,
      data: { status: 0 },
    });
  };

  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t("video.batchSelectWarning"));
      return;
    }
    Modal.confirm({
      title: i18n.t("sales.deleteWarning"),
      content: i18n.t("video.selectedCount", { count: selectedRowKeys.length }),
      onOk: () => {
        setLoading(true);
        batchDeleteMutation.mutate(selectedRowKeys);
      },
      okText: i18n.t("base.modal.okText"),
      cancelText: i18n.t("base.modal.cancelText"),
    });
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: i18n.t("sales.deleteWarning"),
      onOk: () => {
        setLoading(true);
        deleteMutation.mutate({
          resource: "video",
          params: {
            id,
            token: user.token,
          },
        });
      },
      okText: i18n.t("base.modal.okText"),
      cancelText: i18n.t("base.modal.cancelText"),
    });
  };

  const handleStatusChange = (item) => {
    setLoading(true);
    batchUpdateMutation.mutate({
      ids: [item.id],
      data: { status: item.status === 0 ? 1 : 0 },
    });
  };

  const handleEdit = (item) => {
    history.push({
      pathname: `/main/videos/edit`,
      state: { id: item.id },
    });
  };

  const handleView = (item) => {
    history.push({
      pathname: `/main/videos/${item.id}`,
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  const breadCrumbData = [
    {
      href: "/main/videos",
      title: i18n.t("video.title"),
    },
  ];

  return (
    <div className="video-list">
      <BreadCrumb data={breadCrumbData} />
      <Spin spinning={loading || isLoading}>
        <VideoListHeader
          keyword={keyword}
          setKeyword={setKeyword}
          status={status}
          setStatus={setStatus}
          categoryId={categoryId}
          setCategoryId={setCategoryId}
        />
        <div style={{ marginBottom: 16, marginTop: 16 }}>
          <Space>
            <Button
              onClick={handleBatchOnShelf}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.album.batchOnShelf")}
            </Button>
            <Button
              onClick={handleBatchOffShelf}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.album.batchOffShelf")}
            </Button>
            <Button
              onClick={handleBatchSetCategory}
              disabled={selectedRowKeys.length === 0}
            >
              {i18n.t("course.batchSetCategory")}
            </Button>
            <Button
              onClick={handleBatchDelete}
              disabled={selectedRowKeys.length === 0}
              danger
            >
              {i18n.t("course.album.batchDelete")}
            </Button>
            {selectedRowKeys.length > 0 && (
              <span style={{ marginLeft: 8 }}>
                {i18n.t("video.selectedCount", {
                  count: selectedRowKeys.length,
                })}
              </span>
            )}
          </Space>
        </div>
        <List
          pagination={{
            current: page,
            showSizeChanger: true,
            pageSize: perPage,
            pageSizeOptions: ["12", "24", "36", "48"],
            onChange: (newPage, pageSize) => {
              setPage(newPage);
              setPerPage(pageSize);
            },
            onShowSizeChange: (current, size) => {
              setPerPage(size);
              setPage(1);
            },
            showQuickJumper: true,
            total: data?.total || 0,
          }}
          grid={{
            gutter: 12,
            xs: 1,
            sm: 1,
            md: 1,
            lg: 2,
            xl: 3,
            xxl: 4,
          }}
          dataSource={data?.data || []}
          renderItem={(item) => {
            const isSelected = selectedRowKeys.includes(item.id);
            return (
              <List.Item>
                <Card
                  hoverable
                  style={{ flexDirection: "column", position: "relative" }}
                  cover={
                    <Row
                      justify="center"
                      style={{
                        display: "flex",
                        marginTop: 10,
                        marginBottom: 10,
                        position: "relative",
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRowKeys([...selectedRowKeys, item.id]);
                          } else {
                            setSelectedRowKeys(
                              selectedRowKeys.filter((id) => id !== item.id)
                            );
                          }
                        }}
                        style={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          zIndex: 10,
                        }}
                      />
                      <div className="video-list-card-cover">
                        <Image
                          preview={false}
                          alt={i18n.getPropNameByLocale(item, "name")}
                          src={
                            (i18n.isLocaleRTL()
                              ? item.ug_image
                              : item.zh_image) ||
                            item.zh_image ||
                            item.ug_image ||
                            "/placeholder.png"
                          }
                        />
                      </div>
                    </Row>
                  }
                  actions={[
                    <Switch
                      key="status"
                      checkedChildren={<CheckOutlined />}
                      unCheckedChildren={<CloseOutlined />}
                      checked={item.status !== 0}
                      onClick={() => handleStatusChange(item)}
                    />,
                    <EyeOutlined key="view" onClick={() => handleView(item)} />,
                    <EditOutlined
                      key="edit"
                      onClick={() => handleEdit(item)}
                    />,
                    <DeleteOutlined
                      key="delete"
                      onClick={() => handleDelete(item.id)}
                    />,
                  ]}
                >
                  <Meta
                    title={
                      <div>
                        <div>{i18n.getPropNameByLocale(item, "name")}</div>
                        <div style={{ marginTop: 4 }}>
                          <Badge
                            status={item.status === 1 ? "success" : "default"}
                            text={
                              item.status === 1
                                ? i18n.t("course.album.statusOnShelf")
                                : i18n.t("course.album.statusOffShelf")
                            }
                          />
                        </div>
                      </div>
                    }
                    description={
                      <div>
                        <div>
                          {i18n.t("video.duration")}:{" "}
                          {formatDuration(item.duration)}
                        </div>
                        {item.views !== undefined && (
                          <div>
                            {i18n.t("video.views")}: {item.views || 0}
                          </div>
                        )}
                        {item.favorites !== undefined && (
                          <div>
                            {i18n.t("video.favorites")}: {item.favorites || 0}
                          </div>
                        )}
                        {item.video_category && (
                          <div>
                            {i18n.t("course.selectCategory")}:{" "}
                            {i18n.getPropNameByLocale(
                              item.video_category,
                              "name"
                            )}
                          </div>
                        )}
                      </div>
                    }
                  />
                </Card>
              </List.Item>
            );
          }}
        />
      </Spin>
      <Modal
        title={i18n.t("course.batchSetCategory")}
        open={batchCategoryModalVisible}
        onOk={handleBatchCategoryConfirm}
        onCancel={() => {
          setBatchCategoryModalVisible(false);
          setSelectedCategoryId(undefined);
        }}
        okText={i18n.t("base.modal.okText")}
        cancelText={i18n.t("base.modal.cancelText")}
      >
        <Select
          style={{ width: "100%" }}
          placeholder={i18n.t("course.selectCategory")}
          value={selectedCategoryId}
          onChange={(value) => setSelectedCategoryId(value)}
        >
          {categories.map((cat) => (
            <Option key={cat.id} value={cat.id}>
              {i18n.getPropNameByLocale(cat, "name")}
            </Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
};

export default VideoList;
